import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ChartLineUp, Trophy, Heart, ArrowUpRight, ArrowDownRight } from "@phosphor-icons/react";

const ICONS = {
  activation_rate: Trophy,
  retention_30d: ChartLineUp,
  healthy_adoption: Heart,
};

function statusFor(metric) {
  const v = metric.value_pct;
  if (v >= metric.good_threshold) return "good";
  if (v >= metric.warning_threshold) return "warn";
  return "bad";
}

const STATUS_STYLE = {
  good: { ring: "ring-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", bar: "bg-emerald-500", dot: "bg-emerald-500", icon: "text-emerald-600" },
  warn: { ring: "ring-amber-200", bg: "bg-amber-50", text: "text-amber-700", bar: "bg-amber-500", dot: "bg-amber-500", icon: "text-amber-600" },
  bad: { ring: "ring-red-200", bg: "bg-red-50", text: "text-red-700", bar: "bg-red-500", dot: "bg-red-500", icon: "text-red-600" },
};

export default function CSMetricsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/customers/cs-metrics")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-sm text-gray-400">
        Loading metrics…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cs-metrics">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.metrics.map((m) => (
          <MetricHeroCard key={m.key} metric={m} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {data.metrics.map((m) => (
          <BreakdownCard key={m.key} metric={m} dimension="by_tier" title="By Tier" />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {data.metrics.map((m) => (
          <BreakdownCard key={m.key} metric={m} dimension="by_poc" title="By POC" />
        ))}
      </div>
    </div>
  );
}

function MetricHeroCard({ metric }) {
  const Icon = ICONS[metric.key] || ChartLineUp;
  const status = statusFor(metric);
  const s = STATUS_STYLE[status];

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-5 ring-1 ${s.ring}`} data-testid={`metric-${metric.key}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center`}>
            <Icon size={18} weight="fill" className={s.icon} />
          </div>
          <span className="text-sm font-semibold text-gray-900">{metric.label}</span>
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-semibold ${s.text} ${s.bg} px-2 py-0.5 rounded-full border ${s.ring.replace("ring-", "border-")}`}>
          {status === "good" ? "Healthy" : status === "warn" ? "Watch" : "Action"}
        </span>
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-display text-[42px] leading-none font-semibold text-gray-900 tabular-nums">
          {metric.value_pct}
        </span>
        <span className="text-xl text-gray-400 font-medium">%</span>
        <span className="ml-auto text-xs text-gray-500 font-mono">
          {metric.numerator}/{metric.denom}
        </span>
      </div>
      <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${s.bar}`}
          style={{ width: `${metric.value_pct}%` }}
        />
      </div>
      <p className="mt-3 text-xs text-gray-500 leading-relaxed">{metric.description}</p>
      <div className="mt-3 flex items-center gap-3 text-[10px] text-gray-400 font-mono">
        <span className="flex items-center gap-1">
          <ArrowUpRight size={10} className="text-emerald-500" />
          ≥{metric.good_threshold}% healthy
        </span>
        <span className="flex items-center gap-1">
          <ArrowDownRight size={10} className="text-red-500" />
          &lt;{metric.warning_threshold}% action
        </span>
      </div>
    </div>
  );
}

function BreakdownCard({ metric, dimension, title }) {
  const rows = metric.breakdown[dimension] || [];
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`breakdown-${metric.key}-${dimension}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">
          {metric.label} · {title}
        </span>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && (
          <div className="text-xs text-gray-400 italic">No data.</div>
        )}
        {rows.map((r) => {
          const status = r.pct >= metric.good_threshold ? "good" : r.pct >= metric.warning_threshold ? "warn" : "bad";
          const s = STATUS_STYLE[status];
          return (
            <div key={r.key} className="text-xs" data-testid={`breakdown-row-${metric.key}-${dimension}-${r.key}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                  <span className="text-gray-700 font-medium">{r.key}</span>
                </div>
                <span className="font-mono tabular-nums text-gray-700">
                  <span className="font-semibold">{r.pct}%</span>
                  <span className="text-gray-400 ml-1.5">{r.numerator}/{r.denom}</span>
                </span>
              </div>
              <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full ${s.bar}`} style={{ width: `${r.pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
